import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { GET_OWNER_CUSTOMER_ROLES_QUERY } from '../../integrations/allkort/graphql/gql/OwnerQueries';
import { CustomerRole, OwnerCustomerRolesResponse } from '../../integrations/allkort/graphql/interface/Owner';

const ROLES_PER_PAGE = 49;
const MAX_ROLES = 500;

/**
 * Hook for fetching ALL available customer roles from currentOwner with automatic pagination.
 * Fetches 49 at a time and follows hasNextPage until all roles are loaded.
 * Uses cache-first since roles are stable reference data.
 *
 * Usage:
 * const { roles, loading, error, isComplete } = useGetOwnerCustomerRoles();
 */
export const useGetOwnerCustomerRoles = () => {
	const [allRoles, setAllRoles] = useState<CustomerRole[]>([]);
	const [currentCursor, setCurrentCursor] = useState<string | null>(null);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [isComplete, setIsComplete] = useState(false);

	const variables = useMemo(() => ({
		first: ROLES_PER_PAGE,
		after: currentCursor,
		before: null,
		last: null,
	}), [currentCursor]);

	const { data, loading, error } = useQuery<OwnerCustomerRolesResponse>(
		GET_OWNER_CUSTOMER_ROLES_QUERY,
		{
			variables,
			fetchPolicy: 'cache-first',
			skip: isComplete,
		}
	);

	useEffect(() => {
		if (!data?.currentOwner?.customerRoles) return;

		const { nodes, pageInfo } = data.currentOwner.customerRoles;

		if (nodes && nodes.length > 0) {
			setAllRoles(prev => {
				if (!currentCursor) {
					return nodes;
				}
				const existingNames = new Set(prev.map(r => r.claimName));
				const newRoles = nodes.filter(r => !existingNames.has(r.claimName));
				return [...prev, ...newRoles];
			});

			const totalRoles = currentCursor ? allRoles.length + nodes.length : nodes.length;

			if (pageInfo.hasNextPage && totalRoles < MAX_ROLES) {
				setCurrentCursor(pageInfo.endCursor);
				setIsFetchingMore(true);
			} else {
				setIsFetchingMore(false);
				setIsComplete(true);
			}
		} else {
			setIsFetchingMore(false);
			setIsComplete(true);
		}
	}, [data, currentCursor]);

	const refetch = () => {
		setAllRoles([]);
		setCurrentCursor(null);
		setIsFetchingMore(false);
		setIsComplete(false);
	};

	return {
		roles: allRoles,
		loading: loading || isFetchingMore,
		error,
		isComplete,
		refetch,
	};
};
